import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {FolderBreadcrumb} from '../../components/folderBreadcrumbs/folderBreadcrumbs';
import {findParentTags} from '../../helpers/models/tagHelpers';
import {makeMapStateToProps, ResolveStateProps} from '../../helpers/reselectHelpers';
import {TagModel} from '../../models/api/tagModel';
import {AppState} from '../../store/appState';
import {getTags} from '../../store/data/tags/tagsSelectors';
import {SettingsBreadcrumbs} from '../common/settingsBreadcrumbs';
import {SettingsTagsBreadcrumbItem} from './settingsTagsBreadcrumbItem';

/*
 * Props.
 */

interface SettingsTagsNestUnderDialogToplineContainerProps {
  tagId?: number;
  onTagClick: HandlerOf<TagModel | undefined>;
}

/*
 * Selectors.
 */

function getTagId(_state: AppState, props: SettingsTagsNestUnderDialogToplineContainerProps) {
  return props.tagId;
}

function makeGetTag() {
  return createSelector([getTagId, getTags], (tagId, tags) => (tagId ? tags[tagId] : undefined));
}

function makeGetParentTags(getTag: ReturnType<typeof makeGetTag>) {
  return createSelector([getTag, getTags], (tag, tags) => {
    if (!tag) {
      return undefined;
    }

    return [tag, ...findParentTags(tags, tag)].reverse();
  });
}

/*
 * Component.
 */

interface SettingsTagsNestUnderDialogToplineContainerComponentProps
  extends SettingsTagsNestUnderDialogToplineContainerProps,
    ResolveStateProps<typeof mapStateToProps> {}

const SettingsTagsNestUnderDialogToplineContainerComponent: FC<
  SettingsTagsNestUnderDialogToplineContainerComponentProps
> = (props) => {
  const {parentTags} = props;

  const folderPathBreadcrumbs: ReadonlyArray<FolderBreadcrumb> = (parentTags || []).map((parentTag) => ({
    label: <SettingsTagsBreadcrumbItem tag={parentTag} />,
    onBreadcrumbClick: () => props.onTagClick(parentTag),
  }));

  return (
    <SettingsBreadcrumbs
      topLevelBreadcrumb={{
        label: <Trans id="settings-alltags">All</Trans>,
        onBreadcrumbClick: () => props.onTagClick(undefined),
      }}
      breadcrumbs={folderPathBreadcrumbs}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SettingsTagsNestUnderDialogToplineContainerProps>()(() => ({
  parentTags: makeGetParentTags(makeGetTag()),
}));

export const SettingsTagsNestUnderDialogToplineContainer = connect(mapStateToProps)(
  SettingsTagsNestUnderDialogToplineContainerComponent,
);
